import type { CampaignEngineType, CampaignScheduleRow, CampaignStatus } from "../contracts/campaign-schedule";

// ── Campaigns View view-model ────────────────────────────────────────────────

export interface CampaignEngineSummary {
  engine_id: string;
  engine_label: string;
  engine_group_label: string;
  engine_type: CampaignEngineType;
  is_nagra: boolean;
}

export interface CampaignEventSummary {
  event_id: string;
  protected_event_id: string;
  event_label: string;
  event_type_label: string;
  event_start: string;
  event_end: string;
  popularity: number | null;
  collection_group: string | null;
  detection_engines: CampaignEngineSummary[];
  response_engines: CampaignEngineSummary[];
}

export interface CampaignSummary {
  campaign_id: string;
  campaign_label: string;
  campaign_start: string;
  campaign_end: string;
  created_at: string;
  created_by: string;
  status: CampaignStatus;
  event_count: number;
  engine_count: number;
  has_off_streams: boolean;
  events: CampaignEventSummary[];
}

export interface CampaignTenantGroup {
  tenant_id: string;
  tenant_label: string;
  campaigns: CampaignSummary[];
}

// ── Matrix View view-model ───────────────────────────────────────────────────

export interface CampaignMatrixColumn {
  key: string;
  tenant_id: string;
  tenant_label: string;
  channel: string;
}

export interface CampaignMatrixCell {
  active: boolean;
  infringement_types: string[];
  monitored_plateforms: string[];
  harvesting_window_minutes: number[];
  target_attributes: string[];
  response_types: string[];
  response_start_diff: number | null;
  response_end_diff: number | null;
  isp_count: number;
}

export interface CampaignMatrixEngineRow {
  engine_id: string;
  engine_label: string;
  engine_group_label: string;
  engine_type: CampaignEngineType;
  is_nagra: boolean;
  /** Cells keyed by column key. Missing keys mean the engine is inactive there. */
  cells: Record<string, CampaignMatrixCell>;
}

export interface CampaignMatrixEvent {
  event_id: string;
  protected_event_id: string;
  event_label: string;
  event_type_label: string;
  event_start: string;
  event_end: string;
  popularity: number | null;
  collection_group: string | null;
  columns: CampaignMatrixColumn[];
  engines: CampaignMatrixEngineRow[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function campaignStatus(
  campaignStart: string,
  campaignEnd: string,
  nowMs: number,
): CampaignStatus {
  const start = Date.parse(campaignStart);
  const end = Date.parse(campaignEnd);
  if (Number.isFinite(end) && end < nowMs) return "past";
  if (Number.isFinite(start) && start > nowMs) return "future";
  return "active";
}

function parseCampaignTime(value: string): number {
  if (!value) return Number.NaN;
  let iso = value.includes("T") ? value : value.replace(" ", "T");
  if (!/([zZ]|[+-]\d{2}:?\d{2})$/.test(iso)) iso += "Z";
  return new Date(iso).getTime();
}

export function isEventLive(eventStart: string, eventEnd: string, nowMs: number): boolean {
  const start = parseCampaignTime(eventStart);
  const end = parseCampaignTime(eventEnd);
  return Number.isFinite(start) && Number.isFinite(end) && start <= nowMs && nowMs <= end;
}

function columnKey(tenantLabel: string, channel: string): string {
  return `${tenantLabel}_${channel}`;
}

// Nagra engines first, then 3rd-party (is_nagra=false) last, alphabetical within.
function byNagraThenLabel(
  a: { is_nagra: boolean; engine_label: string },
  b: { is_nagra: boolean; engine_label: string },
): number {
  return Number(b.is_nagra) - Number(a.is_nagra) || a.engine_label.localeCompare(b.engine_label);
}

function mergeUnique(target: string[], source: string[]): void {
  for (const value of source) {
    if (value && !target.includes(value)) target.push(value);
  }
}

function mergeUniqueNumbers(target: number[], source: number[]): void {
  for (const value of source) {
    if (!target.includes(value)) target.push(value);
  }
}

/**
 * Build the per-tenant campaign tree used by the Campaigns View. Rows are
 * grouped tenant → campaign → event, engines are de-duplicated per event and
 * split by `engine_type`.
 */
export function buildTenantGroups(
  rows: CampaignScheduleRow[],
  nowMs: number,
): CampaignTenantGroup[] {
  const tenants = new Map<string, CampaignTenantGroup>();
  const campaigns = new Map<string, CampaignSummary>();
  const events = new Map<string, CampaignEventSummary>();
  const eventIdsByCampaign = new Map<string, Set<string>>();
  const engineIdsByCampaign = new Map<string, Set<string>>();
  const engineIdsByEvent = new Map<string, Set<string>>();

  for (const row of rows) {
    let tenant = tenants.get(row.tenant_id);
    if (!tenant) {
      tenant = {
        tenant_id: row.tenant_id,
        tenant_label: row.tenant_label,
        campaigns: [],
      };
      tenants.set(row.tenant_id, tenant);
    }

    let campaign = campaigns.get(row.campaign_id);
    if (!campaign) {
      campaign = {
        campaign_id: row.campaign_id,
        campaign_label: row.campaign_label,
        campaign_start: row.campaign_start,
        campaign_end: row.campaign_end,
        created_at: row.created_at,
        created_by: row.created_by,
        status: campaignStatus(row.campaign_start, row.campaign_end, nowMs),
        event_count: 0,
        engine_count: 0,
        has_off_streams: false,
        events: [],
      };
      campaigns.set(row.campaign_id, campaign);
      eventIdsByCampaign.set(row.campaign_id, new Set());
      engineIdsByCampaign.set(row.campaign_id, new Set());
      tenant.campaigns.push(campaign);
    }

    if (row.off_stream_ids.length > 0) campaign.has_off_streams = true;

    const eventKey = `${row.campaign_id}_${row.event_id}`;
    let event = events.get(eventKey);
    if (!event) {
      event = {
        event_id: row.event_id,
        protected_event_id: row.protected_event_id,
        event_label: row.event_label,
        event_type_label: row.event_type_label,
        event_start: row.event_start,
        event_end: row.event_end,
        popularity: row.popularity,
        collection_group: row.collection_group,
        detection_engines: [],
        response_engines: [],
      };
      events.set(eventKey, event);
      engineIdsByEvent.set(eventKey, new Set());
      campaign.events.push(event);
      eventIdsByCampaign.get(row.campaign_id)?.add(row.event_id);
    }

    if (row.engine_id) {
      engineIdsByCampaign.get(row.campaign_id)?.add(row.engine_id);
      const eventEngineIds = engineIdsByEvent.get(eventKey);
      if (eventEngineIds && !eventEngineIds.has(row.engine_id)) {
        eventEngineIds.add(row.engine_id);
        const summary: CampaignEngineSummary = {
          engine_id: row.engine_id,
          engine_label: row.engine_label,
          engine_group_label: row.engine_group_label,
          engine_type: row.engine_type,
          is_nagra: row.is_nagra,
        };
        if (row.engine_type === "response") event.response_engines.push(summary);
        else event.detection_engines.push(summary);
      }
    }
  }

  for (const [campaignId, campaign] of campaigns) {
    campaign.event_count = eventIdsByCampaign.get(campaignId)?.size ?? 0;
    campaign.engine_count = engineIdsByCampaign.get(campaignId)?.size ?? 0;
    campaign.events.sort((a, b) => a.event_start.localeCompare(b.event_start));
    for (const event of campaign.events) {
      event.detection_engines.sort(byNagraThenLabel);
      event.response_engines.sort(byNagraThenLabel);
    }
  }

  for (const tenant of tenants.values()) {
    tenant.campaigns.sort((a, b) => b.campaign_start.localeCompare(a.campaign_start));
  }

  return [...tenants.values()].sort((a, b) => a.tenant_label.localeCompare(b.tenant_label));
}

/**
 * Build one matrix per event. Columns are the distinct tenant/channel pairs and
 * rows are the engines active for the event, detection engines first.
 */
export function buildMatrixEvents(rows: CampaignScheduleRow[]): CampaignMatrixEvent[] {
  const events = new Map<string, CampaignMatrixEvent>();
  const columnKeys = new Map<string, Set<string>>();
  const engineIndex = new Map<string, Map<string, CampaignMatrixEngineRow>>();

  for (const row of rows) {
    let event = events.get(row.event_id);
    if (!event) {
      event = {
        event_id: row.event_id,
        protected_event_id: row.protected_event_id,
        event_label: row.event_label,
        event_type_label: row.event_type_label,
        event_start: row.event_start,
        event_end: row.event_end,
        popularity: row.popularity,
        collection_group: row.collection_group,
        columns: [],
        engines: [],
      };
      events.set(row.event_id, event);
      columnKeys.set(row.event_id, new Set());
      engineIndex.set(row.event_id, new Map());
    }

    const key = columnKey(row.tenant_label, row.channel);
    const seenColumns = columnKeys.get(row.event_id);
    if (seenColumns && !seenColumns.has(key)) {
      seenColumns.add(key);
      event.columns.push({
        key,
        tenant_id: row.tenant_id,
        tenant_label: row.tenant_label,
        channel: row.channel,
      });
    }

    if (!row.engine_id) continue;

    const enginesForEvent = engineIndex.get(row.event_id);
    if (!enginesForEvent) continue;
    let engine = enginesForEvent.get(row.engine_id);
    if (!engine) {
      engine = {
        engine_id: row.engine_id,
        engine_label: row.engine_label,
        engine_group_label: row.engine_group_label,
        engine_type: row.engine_type,
        is_nagra: row.is_nagra,
        cells: {},
      };
      enginesForEvent.set(row.engine_id, engine);
      event.engines.push(engine);
    }

    let cell = engine.cells[key];
    if (!cell) {
      cell = {
        active: true,
        infringement_types: [],
        monitored_plateforms: [],
        harvesting_window_minutes: [],
        target_attributes: [],
        response_types: [],
        response_start_diff: null,
        response_end_diff: null,
        isp_count: 0,
      };
      engine.cells[key] = cell;
    }
    mergeUnique(cell.infringement_types, row.infringement_types);
    mergeUnique(cell.monitored_plateforms, row.monitored_plateforms);
    mergeUniqueNumbers(cell.harvesting_window_minutes, row.harvesting_window_minutes);
    mergeUnique(cell.target_attributes, row.target_attributes);
    mergeUnique(cell.response_types, row.response_types);
    cell.response_start_diff ??= row.response_start_diff;
    cell.response_end_diff ??= row.response_end_diff;
    cell.isp_count = Math.max(cell.isp_count, row.expected_contact_names.length);
  }

  const typeRank: Record<CampaignEngineType, number> = { detection: 0, response: 1 };

  for (const event of events.values()) {
    event.columns.sort(
      (a, b) => a.tenant_label.localeCompare(b.tenant_label) || a.channel.localeCompare(b.channel),
    );
    event.engines.sort(
      (a, b) =>
        typeRank[a.engine_type] - typeRank[b.engine_type] ||
        Number(b.is_nagra) - Number(a.is_nagra) ||
        a.engine_group_label.localeCompare(b.engine_group_label) ||
        a.engine_label.localeCompare(b.engine_label),
    );
  }

  return [...events.values()].sort((a, b) => b.event_start.localeCompare(a.event_start));
}
